/**
 * @fileoverview Control panels: system parameters and the selected room.
 *
 * Every control carries just-in-time procedural help (4C/ID component 3):
 * a compact "how to use this" popover exactly where the action happens.
 * Mission scaffolding can lock any control.
 *
 * Layout is organised into labelled sub-sections (Weather, Supply temperature,
 * Circulation pump, Pipe network / Room design) for fast scanning; the controls
 * and their help text are unchanged.
 *
 * @module HydraulicBalancingTesting/components/ControlsPanel
 */

import React from 'react';
import { Slider, Select, Radio, Switch, Popover, Tag, Button, Popconfirm } from 'antd';
import { QuestionCircleOutlined, LockOutlined } from '@ant-design/icons';

import {
  PRESET_POSITIONS,
  RADIATOR,
  HEAT_PUMP,
  PUMP,
  OUTDOOR,
  SCORING,
  PIPES,
} from '../constants';
import { toLitersPerHour } from '../physics';
import { HeatingCurvePreview, TempSparkline } from './Charts';

import styles from '../HydraulicBalancingTesting.module.css';

// ----------------------------------------------------------------------------
// JIT help label
// ----------------------------------------------------------------------------

const JitLabel = ({ text, help, locked }) => (
  <span className={styles.controlLabel}>
    {text}
    {locked && <LockOutlined style={{ marginLeft: 6, color: '#bbb' }} />}
    {help && (
      <Popover content={<div style={{ maxWidth: 290 }}>{help}</div>} title={text} trigger="hover">
        <QuestionCircleOutlined style={{ marginLeft: 6, color: '#1890ff', cursor: 'help' }} />
      </Popover>
    )}
  </span>
);

/** A labelled group of related controls. */
const Section = ({ title, children }) => (
  <div className={styles.controlSection}>
    <div className={styles.controlSectionTitle}>{title}</div>
    {children}
  </div>
);

// ----------------------------------------------------------------------------
// System controls
// ----------------------------------------------------------------------------

export const SystemControls = ({ engine, isSystemEditable, onOpenPipeEditor, onOpenPipeTheory }) => {
  const { params, setParam } = engine;

  const lockedOutdoor = !isSystemEditable('tOutdoor');
  const lockedMode = !isSystemEditable('supplyMode');
  const lockedFixed = !isSystemEditable('tSupplyFixed');
  const lockedSlope = !isSystemEditable('curveSlope');
  const lockedOffset = !isSystemEditable('curveOffset');
  const lockedPump = !isSystemEditable('pumpHead');
  const lockedBalancing = !isSystemEditable('balancingMode');

  // pipe-network summary (uniform diameter per group, else "mixed")
  const uniformDia = (segs) => {
    const ds = segs.map((s) => s.diameter);
    return ds.every((d) => d === ds[0]) ? `${(ds[0] * 1000).toFixed(0)} mm` : 'mixed';
  };
  const pipeSummary = `Risers ${uniformDia(params.pipes.risers)} · Floors ${uniformDia(params.pipes.floors.flat())} · Branches ${uniformDia(params.pipes.branches)}`;

  return (
    <div className={styles.panelCard}>
      <p className={styles.panelTitle}>System</p>

      <div className={styles.systemGrid}>
        {/* Weather */}
        <div className={styles.systemField}>
          <JitLabel
            text={`Outdoor: ${params.tOutdoor.toFixed(0)} °C`}
            locked={lockedOutdoor}
            help="The weather. Heat losses grow with the gap between room and outdoor temperature. The design point for the building's location is −10 °C."
          />
          <Slider
            min={OUTDOOR.MIN}
            max={OUTDOOR.MAX}
            step={1}
            value={params.tOutdoor}
            disabled={lockedOutdoor}
            onChange={(v) => setParam('tOutdoor', v)}
            marks={{ [-10]: 'design', 0: '0°' }}
          />
        </div>

        {/* Circulation pump */}
        <div className={styles.systemField}>
          <JitLabel
            text={`Pump head: ${params.pumpHead.toFixed(0)} kPa`}
            locked={lockedPump}
            help="The differential pressure setting of the circulation pump. More head = more flow everywhere = more pump electricity and noise. After balancing you can usually lower it."
          />
          <Slider
            min={PUMP.MIN_HEAD_KPA}
            max={PUMP.MAX_HEAD_KPA}
            step={1}
            value={params.pumpHead}
            disabled={lockedPump}
            onChange={(v) => setParam('pumpHead', v)}
          />
        </div>

        {/* Supply mode */}
        <div className={styles.systemField}>
          <JitLabel
            text="Supply control"
            locked={lockedMode}
            help="Fixed: the heat pump always produces the same supply temperature. Heating curve: the supply follows the weather — colder outside, warmer water. Real heat pumps always run a curve."
          />
          <Radio.Group
            value={params.supplyMode}
            disabled={lockedMode}
            onChange={(e) => setParam('supplyMode', e.target.value)}
            size="small"
            style={{ display: 'block', marginTop: 2 }}
          >
            <Radio.Button value="fixed">Fixed</Radio.Button>
            <Radio.Button value="curve">Curve</Radio.Button>
          </Radio.Group>
        </div>

        {/* Commissioning */}
        <div className={styles.systemField}>
          <JitLabel
            text="Commissioning"
            locked={lockedBalancing}
            help="Removes all thermostatic heads, i.e. forces every TRV fully open. This is how installers measure and set presets in the field: you see the raw hydraulics without the thermostats compensating."
          />
          <div style={{ marginTop: 4 }}>
            <Switch
              checked={params.balancingMode}
              disabled={lockedBalancing}
              onChange={(v) => setParam('balancingMode', v)}
              size="small"
            />
          </div>
        </div>

        {/* Supply temperature detail (mode dependent) */}
        {params.supplyMode === 'fixed' ? (
          <div className={styles.systemFieldFull}>
            <JitLabel
              text={`Supply temperature: ${params.tSupplyFixed.toFixed(0)} °C`}
              locked={lockedFixed}
              help="The water temperature leaving the heat pump. Lower = better COP (≈2–3% per Kelvin), but the radiators must still cover the rooms' heat losses."
            />
            <Slider
              min={HEAT_PUMP.MIN_SUPPLY}
              max={HEAT_PUMP.MAX_SUPPLY}
              step={1}
              value={params.tSupplyFixed}
              disabled={lockedFixed}
              onChange={(v) => setParam('tSupplyFixed', v)}
              marks={{ 45: 'design' }}
            />
          </div>
        ) : (
          <>
            <div className={styles.systemField}>
              <JitLabel
                text={`Curve slope: ${params.curveSlope.toFixed(2)}`}
                locked={lockedSlope}
                help="How steeply the supply temperature rises as it gets colder outside. T_sup = 20 + offset + slope·(20 − T_out). Balanced buildings allow flatter curves."
              />
              <Slider
                min={HEAT_PUMP.CURVE_SLOPE_MIN}
                max={HEAT_PUMP.CURVE_SLOPE_MAX}
                step={0.01}
                value={params.curveSlope}
                disabled={lockedSlope}
                onChange={(v) => setParam('curveSlope', v)}
              />
            </div>
            <div className={styles.systemField}>
              <JitLabel
                text={`Curve offset: ${params.curveOffset.toFixed(1)} K`}
                locked={lockedOffset}
                help="Parallel shift of the whole curve. Use small offsets for fine-tuning after the slope is right."
              />
              <Slider
                min={HEAT_PUMP.CURVE_OFFSET_MIN}
                max={HEAT_PUMP.CURVE_OFFSET_MAX}
                step={0.5}
                value={params.curveOffset}
                disabled={lockedOffset}
                onChange={(v) => setParam('curveOffset', v)}
              />
            </div>
            <div className={styles.systemFieldFull} style={{ display: 'flex', justifyContent: 'center' }}>
              <HeatingCurvePreview slope={params.curveSlope} offset={params.curveOffset} tOutdoor={params.tOutdoor} />
            </div>
          </>
        )}

        {/* Pipe network (full width) — summary + editor button */}
        <div className={styles.systemFieldFull}>
          <div className={styles.controlRow}>
            <span className={styles.controlSectionTitle} style={{ margin: 0 }}>
              Pipe network{' '}
              <QuestionCircleOutlined
                onClick={onOpenPipeTheory}
                style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 400 }}
                title="Pipe theory (friction & the D⁵ rule)"
              />
            </span>
          </div>
          <div className={styles.pipeSummaryRow}>
            <span className={styles.pipeTierLabel}>{pipeSummary}</span>
            <Button size="small" type="primary" ghost onClick={onOpenPipeEditor}>
              Open pipe network editor
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Selected-room controls
// ----------------------------------------------------------------------------

export const RoomControls = ({
  engine,
  roomIdx,
  showTargets,
  presetEditable,
  sandbox,
}) => {
  const room = engine.rooms[roomIdx];
  const { derived } = engine;
  if (!room) return null;

  const dev = derived.deviations[roomIdx];
  const liveFlow = derived.network.roomFlows[roomIdx];
  const liveVelocity = derived.network.velocity[roomIdx];
  const liveAuthority = derived.network.authority[roomIdx];
  const flowLph = toLitersPerHour(liveFlow);
  const designLph = toLitersPerHour(room.designFlow);
  const noisy = liveVelocity > PIPES.NOISE_VELOCITY;
  const lowAuthority = liveAuthority > 0 && liveAuthority < SCORING.MIN_AUTHORITY;
  const spread = derived.tSupply - room.Treturn;
  const spreadGood = spread >= 4;
  const tempErr = room.T - room.Tset;

  // header status chip
  const tempTag =
    Math.abs(tempErr) <= 0.5
      ? { color: 'success', label: 'at setpoint' }
      : tempErr > 0
      ? { color: 'error', label: `+${tempErr.toFixed(1)} °C warm` }
      : { color: 'processing', label: `${tempErr.toFixed(1)} °C cool` };

  // one-line balancing diagnosis (commissioning deviation; only when targets shown)
  let hint = null;
  if (showTargets) {
    if (dev > 0.1) hint = { color: '#e2574c', msg: 'Overflowing — lower this valve preset so the starved rooms get their share.' };
    else if (dev < -0.1) hint = { color: '#1890ff', msg: 'Starving — raise this preset (and throttle the overflowing rooms to free up pressure).' };
    else hint = { color: '#52c41a', msg: 'Balanced — flow is within ±10% of the design flow.' };
  }

  return (
    <div className={styles.panelCard}>
      <div className={styles.roomHeaderRow}>
        <p className={styles.panelTitle} style={{ margin: 0 }}>
          R{room.id} · {room.name}
        </p>
        <span className={styles.roomTemp}>{room.T.toFixed(1)} °C</span>
      </div>
      <div className={styles.roomStatusRow}>
        <Tag color={tempTag.color} style={{ margin: 0 }}>{tempTag.label}</Tag>
        <span className={styles.infoLabelHbt}>setpoint {room.Tset.toFixed(1)} °C</span>
      </div>

      <Section title="Balancing">
        {/* hero control: preset */}
        <JitLabel
          text={`Valve preset: position ${PRESET_POSITIONS[room.presetIndex].label} (Kv ${PRESET_POSITIONS[room.presetIndex].kv.toFixed(2)})`}
          locked={!presetEditable}
          help={
            <span>
              The presetting ring under the thermostatic head. It limits the valve's maximum Kv:
              position 1 ≈ 0.04 m³/h·√bar (strong restriction) … N ≈ 0.73 (fully open).
              <b> Overflowing room → lower position. Starving room → higher position.</b> Every
              change re-routes flow through the WHOLE network.
            </span>
          }
        />
        <Slider
          min={0}
          max={PRESET_POSITIONS.length - 1}
          step={1}
          value={room.presetIndex}
          disabled={!presetEditable}
          onChange={(v) => engine.setRoomPreset(roomIdx, v)}
          tooltip={{ formatter: (v) => `pos ${PRESET_POSITIONS[v].label} · Kv ${PRESET_POSITIONS[v].kv.toFixed(2)}` }}
          marks={{ 0: '1', 4: '3', 8: '5', 12: '7', 13: 'N' }}
        />

        {hint && (
          <div className={styles.roomHint} style={{ borderLeftColor: hint.color }}>
            {hint.msg}
          </div>
        )}

        {/* live hydraulic data */}
        <div className={styles.infoGridHbt}>
          <div>
            <span className={styles.infoLabelHbt}>Actual flow</span>
            <b>{flowLph.toFixed(0)} l/h</b>
          </div>
          {showTargets && (
            <div>
              <span className={styles.infoLabelHbt}>Design flow</span>
              <b>{designLph.toFixed(0)} l/h</b>
            </div>
          )}
          {showTargets && (
            <div>
              <span className={styles.infoLabelHbt}>Deviation</span>
              <b style={{ color: Math.abs(dev) <= 0.1 ? '#52c41a' : dev > 0 ? '#e2574c' : '#1890ff' }}>
                {dev >= 0 ? '+' : ''}{(dev * 100).toFixed(0)}%
              </b>
            </div>
          )}
          <div>
            <span className={styles.infoLabelHbt}>Return temp</span>
            <b>{room.Treturn.toFixed(1)} °C</b>
          </div>
          <div>
            <span className={styles.infoLabelHbt}>
              Spread{' '}
              <Popover
                content={<div style={{ maxWidth: 260 }}>Supply minus this radiator's return temperature. Heat-pump systems aim for ≈5 K; a small spread means overflow — water races through without cooling, raising the return and hurting the COP.</div>}
              >
                <QuestionCircleOutlined style={{ color: '#1890ff' }} />
              </Popover>
            </span>
            <b style={spreadGood ? undefined : { color: '#faad14' }}>{spread.toFixed(1)} °C</b>
          </div>
          <div>
            <span className={styles.infoLabelHbt}>Radiator output</span>
            <b>{room.qrad.toFixed(0)} W</b>
          </div>
          <div>
            <span className={styles.infoLabelHbt}>Heat load (design)</span>
            <b>{room.designLoad.toFixed(0)} W</b>
          </div>
          <div>
            <span className={styles.infoLabelHbt}>
              Valve authority{' '}
              <Popover
                content={<div style={{ maxWidth: 260 }}>Share of the branch pressure drop across the valve (a = Δp_valve/Δp_branch). Below ~0.3 the valve barely controls its branch.</div>}
              >
                <QuestionCircleOutlined style={{ color: '#1890ff' }} />
              </Popover>
            </span>
            <b style={lowAuthority ? { color: '#faad14' } : undefined}>{(liveAuthority * 100).toFixed(0)}%</b>
          </div>
          <div>
            <span className={styles.infoLabelHbt}>
              Velocity{' '}
              <Popover
                content={<div style={{ maxWidth: 260 }}>Water speed in this branch's pipe. Above ≈{PIPES.NOISE_VELOCITY} m/s valves and fittings start to whistle — the 🔊 marks a branch that is likely noisy, usually because it is overflowing. Lower its preset (or the pump head) to quiet it.</div>}
              >
                <QuestionCircleOutlined style={{ color: '#1890ff' }} />
              </Popover>
            </span>
            <b style={noisy ? { color: '#ff4d4f' } : undefined}>
              {liveVelocity.toFixed(2)} m/s{noisy ? ' 🔊' : ''}
            </b>
          </div>
        </div>
      </Section>

      <Section title="Room design">
        <div className={styles.controlRow}>
          <JitLabel
            text={`Room setpoint: ${room.Tset.toFixed(1)} °C`}
            locked={!sandbox}
            help="The thermostatic head's target temperature. DIN EN 12831 suggests 20 °C living, 24 °C bath, 18 °C bedroom. Changing it changes the room's design heat load and design flow."
          />
          <Popconfirm
            title={`Set all 9 rooms to ${room.Tset.toFixed(1)} °C?`}
            okText="Apply"
            cancelText="Cancel"
            disabled={!sandbox}
            onConfirm={() => engine.setAllRoomsProp('Tset', room.Tset)}
          >
            <Button size="small" type="link" disabled={!sandbox} className={styles.applyAllBtn}>
              Apply to all
            </Button>
          </Popconfirm>
        </div>
        <Slider
          min={15}
          max={26}
          step={0.5}
          value={room.Tset}
          disabled={!sandbox}
          onChange={(v) => engine.setRoomProp(roomIdx, 'Tset', v)}
        />

        <div className={styles.controlRow}>
          <JitLabel
            text={`Radiator size: ${room.radiatorNominal} W @ ΔT50`}
            locked={!sandbox}
            help="Catalogue output rated at ΔT50 (EN 442: 75/65 °C water into a 20 °C room → mean 70 − 20 = 50 K). Output scales with the mean over-temperature as Q ∝ ΔT^1.3, so at heat-pump temperatures (45/40 °C → ΔT 22.5 K) a radiator delivers only ≈35% of nominal — that is why heat-pump retrofits need generous radiators."
          />
          <span className={styles.infoLabelHbt}>now {room.qrad.toFixed(0)} W</span>
        </div>
        <Select
          size="small"
          style={{ width: '100%', marginBottom: 8 }}
          value={room.radiatorNominal}
          disabled={!sandbox}
          onChange={(v) => engine.setRoomProp(roomIdx, 'radiatorNominal', v)}
          options={RADIATOR.SIZES.map((s) => ({ value: s, label: `${s} W` }))}
        />

        <div className={styles.controlRow}>
          <JitLabel
            text={`Envelope U-value: ${room.Uout.toFixed(2)} W/(m²·K)`}
            locked={!sandbox}
            help="Quality of this room's exterior insulation. Higher U = more heat loss = larger design load and design flow."
          />
          <Popconfirm
            title={`Set all 9 rooms to U = ${room.Uout.toFixed(2)} W/(m²·K)?`}
            okText="Apply"
            cancelText="Cancel"
            disabled={!sandbox}
            onConfirm={() => engine.setAllRoomsProp('Uout', room.Uout)}
          >
            <Button size="small" type="link" disabled={!sandbox} className={styles.applyAllBtn}>
              Apply to all
            </Button>
          </Popconfirm>
        </div>
        <Slider
          min={0.2}
          max={1.6}
          step={0.05}
          value={room.Uout}
          disabled={!sandbox}
          onChange={(v) => engine.setRoomProp(roomIdx, 'Uout', v)}
        />
      </Section>

      <TempSparkline room={room} width={360} height={86} />
    </div>
  );
};
